import React, { useState, useRef, useEffect } from 'react';
import { X, Star, MapPin, Clock, Car, Navigation, Heart, Share2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ParkAndRideLocation } from '../types';

interface ParkAndRideModalProps {
  details: ParkAndRideLocation | null;
  handleClose: () => void;
}

const ParkAndRideModal: React.FC<ParkAndRideModalProps> = ({ details, handleClose }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'parking' | 'directions'>('overview');

  const headerRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Measure header and tabs height for content positioning
  useEffect(() => {
    if (!details) return;
    const updateHeights = () => {
      if (headerRef.current && tabsRef.current && modalRef.current) {
        const headerHeight = headerRef.current.offsetHeight;
        const tabsHeight = tabsRef.current.offsetHeight;
        modalRef.current.style.setProperty('--modal-header-height', `${headerHeight}px`);
        modalRef.current.style.setProperty('--modal-tabs-height', `${tabsHeight}px`);
      }
    };

    updateHeights();
    window.addEventListener('resize', updateHeights);
    return () => window.removeEventListener('resize', updateHeights);
  }, [details?.name]);

  if (!details) return null;

  const getBusynessColor = (busyness: number): string => {
    if (busyness < 30) return '#4CAF50'; // Green - Low busyness
    if (busyness < 60) return '#FF9800'; // Orange - Medium busyness
    if (busyness < 80) return '#FF5722'; // Red-Orange - High busyness
    return '#F44336'; // Red - Very high busyness
  };

  const getBusynessText = (busyness: number): string => {
    if (busyness < 30) return 'Low';
    if (busyness < 60) return 'Medium';
    if (busyness < 80) return 'High';
    return 'Very High';
  };

  const getParkingTime = (busyness: number): string => {
    if (busyness < 30) return '< 2 mins';
    if (busyness < 60) return '2 - 5 mins';
    if (busyness < 80) return '5 - 10 mins';
    return '> 10 mins';
  };

  const getGoogleMapsUrl = (): string => {
    // Ensure coordinates are valid numbers
    if (typeof details.latitude === 'number' && typeof details.longitude === 'number') {
      return `https://www.google.com/maps/dir/?api=1&destination=${details.latitude},${details.longitude}`;
    }
    // Fallback to a default location if coordinates are invalid
    return `https://www.google.com/maps/dir/?api=1&destination=-33.8688,151.2093`;
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        size={16}
        className={i < Math.floor(rating) ? 'star-filled' : 'star-empty'}
        fill={i < Math.floor(rating) ? '#FFD700' : 'none'}
      />
    ));
  };

  const handleSaveLocation = () => {
    // TODO: Implement save functionality
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `Check out ${details.name} on VibeCheck!`,
        text: `I found this Park & Ride facility with ${details.availability} available spots and ${details.busyness}% crowd level!`,
        url: window.location.href,
      }).catch((error) => {
        // Silently handle share cancellation or errors
        if (error.name !== 'AbortError') {
          console.log('Share error:', error);
        }
      });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="modal-overlay fullscreen"
      onClick={handleClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        ref={modalRef}
        className="location-modal fullscreen"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div ref={headerRef} className="modal-header fullscreen">
          <div className="header-content">
            <h1 className="location-title">{details.name}</h1>
            <div className="location-meta">
              <span className="category-badge">{details.category}</span>
              <div className="rating-container">
                {renderStars(details.vibeRating || 4.0)}
                <span className="rating-text">{details.vibeRating || 4.0}/5</span>
              </div>
            </div>
          </div>
          <div className="header-actions">
            <button onClick={handleSaveLocation} className="action-button">
              <Heart size={20} />
            </button>
            <button onClick={handleShare} className="action-button">
              <Share2 size={20} />
            </button>
            <button onClick={handleClose} className="close-button">
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div ref={tabsRef} className="modal-tabs">
          {[
            { id: 'overview', label: 'Overview', icon: MapPin },
            { id: 'parking', label: 'Parking Info', icon: Car },
            { id: 'directions', label: 'Directions', icon: Navigation },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id as any)}
              className={`tab-button ${activeTab === id ? 'active' : ''}`}
            >
              <Icon size={18} />
              <span>{label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="modal-content fullscreen">
          <AnimatePresence mode="wait">
            {activeTab === 'overview' && (
              <motion.div
                key="overview"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="tab-content"
              >
                <div className="overview-section">
                  <div className="busyness-indicator">
                    <div
                      className="busyness-circle"
                      style={{
                        background: `linear-gradient(135deg, ${getBusynessColor(details.busyness || 0)} 0%, ${getBusynessColor(Math.min((details.busyness || 0) + 10, 100))} 100%)`,
                      }}
                    >
                      <span className="busyness-percentage">{details.busyness || 0}%</span>
                      <span className="busyness-label">Busy</span>
                    </div>
                    <div className="busyness-details">
                      <p>
                        Available spots: <strong>{details.availability}</strong>
                      </p>
                      <p>
                        Parking time: <strong>{getParkingTime(details.busyness || 0)}</strong>
                      </p>
                    </div>
                  </div>

                  <button
                    className="directions-button"
                    onClick={() => window.open(getGoogleMapsUrl(), '_blank')}
                    title="Open Google Maps with directions to this Park & Ride facility"
                  >
                    <Navigation size={18} />
                    <span>Get Directions</span>
                  </button>
                </div>

                {/* Key Stats Cards */}
                <div className="stats-grid">
                  <div className="stat-card">
                    <div className="stat-icon">
                      <Car className="w-6 h-6" />
                    </div>
                    <div className="stat-content">
                      <h3>Total Spots</h3>
                      <p className="stat-value">{details.total_spots}</p>
                    </div>
                  </div>

                  <div className="stat-card">
                    <div className="stat-icon">
                      <MapPin className="w-6 h-6" />
                    </div>
                    <div className="stat-content">
                      <h3>Available</h3>
                      <p className="stat-value">{details.availability}</p>
                    </div>
                  </div>

                  <div className="stat-card">
                    <div className="stat-icon">
                      <Clock className="w-6 h-6" />
                    </div>
                    <div className="stat-content">
                      <h3>Occupied</h3>
                      <p className="stat-value">{details.occupied_spots}</p>
                    </div>
                  </div>
                </div>

                {/* Location Details */}
                <div className="details-section">
                  <h3>Location Details</h3>
                  <div className="details-grid">
                    <div className="detail-item">
                      <span className="detail-label">Coordinates</span>
                      <span className="detail-value">
                        {typeof details.latitude === 'number' ? details.latitude.toFixed(6) : details.latitude},{' '}
                        {typeof details.longitude === 'number' ? details.longitude.toFixed(6) : details.longitude}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Facility ID</span>
                      <span className="detail-value">{details.facility_id}</span>
                    </div>
                  </div>
                </div>

                {/* Powered by (match Mobility modal structure) */}
                <div className="powered-by-sydney-card">
                  <div className="powered-by-content">
                    <div className="powered-by-text">Powered by</div>
                    <div className="city-of-sydney-logo">
                      <img src="/nsw-government-logo.svg" alt="NSW Government Logo" className="city-logo-image" />
                      <span className="city-name">Transport for NSW</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {activeTab === 'parking' && (
              <motion.div
                key="parking"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="tab-content"
              >
                <div className="parking-section">
                  <div className="capacity-overview">
                    <h3>Parking Capacity</h3>
                    <div className="capacity-bar">
                      <div
                        className="capacity-fill"
                        style={{
                          width: `${(() => {
                            if (
                              typeof details.total_spots === 'number' &&
                              typeof details.availability === 'number' &&
                              details.total_spots > 0
                            ) {
                              return ((details.total_spots - details.availability) / details.total_spots) * 100;
                            }
                            return 0;
                          })()}%`,
                          backgroundColor: getBusynessColor(details.busyness || 0),
                        }}
                      ></div>
                    </div>
                    <div className="capacity-stats">
                      <span>Available: {details.availability} spots</span>
                      <span>Occupied: {details.occupied_spots} spots</span>
                    </div>
                  </div>

                  <div className="parking-tips">
                    <h3>Parking Tips</h3>
                    <ul>
                      <li>Park & Ride facilities are typically free for public transport users</li>
                      <li>Arrive early during peak hours for better availability</li>
                      <li>Check transport timetables before planning your trip</li>
                      <li>Consider alternative transport during major events</li>
                    </ul>
                  </div>
                </div>
              </motion.div>
            )}

            {activeTab === 'directions' && (
              <motion.div
                key="directions"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="tab-content"
              >
                <div className="directions-section">
                  <div className="transport-options">
                    <h3>Getting There</h3>

                    <div className="transport-option">
                      <h4>By Car</h4>
                      <p>Navigate to this Park & Ride facility using the directions button below.</p>
                      <button
                        className="directions-button secondary"
                        onClick={() => window.open(getGoogleMapsUrl(), '_blank')}
                      >
                        <Navigation size={18} />
                        <span>Open in Google Maps</span>
                      </button>
                    </div>

                    <div className="transport-option">
                      <h4>By Public Transport</h4>
                      <p>
                        This facility is designed for Park & Ride, so you can drive here and then continue your journey
                        using public transport.
                      </p>
                    </div>

                    <div className="facility-features">
                      <h4>Facility Features</h4>
                      <ul>
                        <li>Secure parking area</li>
                        <li>CCTV surveillance</li>
                        <li>Well-lit facilities</li>
                        <li>Easy access to public transport</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ParkAndRideModal;
